import { useState } from 'react';
import { RouterProvider, type RouterHistory } from '@tanstack/react-router';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import { api } from '@aeon-karaoke/cloud-convex';
import { AccountGate } from '@/component/AccountGate';
import { AppErrorBoundary } from '@/component/AppErrorBoundary';
import { convex } from '@/lib/convex';
import { createAppRouter, type RouterContext } from '@/router';

/**
 * Convex writes a mutation's query results into the client before its promise
 * resolves, so this is current the moment a save returns, even before React
 * re-renders. Answers only while AccountGate holds the `users.me` subscription.
 */
const readAccount = () => convex.watchQuery(api.users.me).localQueryResult() ?? null;

const noAccount = () => null;

export default function App({ history }: { history: RouterHistory }) {
  return (
    <>
      <SignedOut>
        <AppRoutes history={history} account={noAccount} />
      </SignedOut>
      <SignedIn>
        <AppErrorBoundary>
          <AccountGate>
            <AppRoutes history={history} account={readAccount} />
          </AccountGate>
        </AppErrorBoundary>
      </SignedIn>
    </>
  );
}

/** A new router per mount. One that had already resolved an address would not
 * run its guards again for whoever signs in next. */
function AppRoutes({ history, account }: { history: RouterHistory } & RouterContext) {
  const [router] = useState(() => createAppRouter(history, account));
  return <RouterProvider router={router} />;
}
